import { ReleaseNoteRecipient, ReleaseNoteSendBody, ReleaseNoteSendResult } from '../../../types';

/** The server refuses more than this in one send. */
export const MAX_RECIPIENTS = 120;

export function errorMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof message === 'string' && message ? message : fallback;
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function filterRecipients(recipients: ReleaseNoteRecipient[], search: string): ReleaseNoteRecipient[] {
  const term = search.trim().toLowerCase();
  if (!term) return recipients;
  return recipients.filter((r) => r.name.toLowerCase().includes(term) || r.email.toLowerCase().includes(term));
}

/** Someone who already got this version can only be picked when sending again. */
export function canPick(recipient: ReleaseNoteRecipient, resend: boolean): boolean {
  return resend || recipient.sentAt === null;
}

export function toggleId(selected: string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id];
}

/** Drops anyone who can no longer be picked, e.g. after "send again" is switched off. */
export function pickableOnly(selected: string[], recipients: ReleaseNoteRecipient[], resend: boolean): string[] {
  const allowed = new Set(recipients.filter((r) => canPick(r, resend)).map((r) => r.id));
  return selected.filter((id) => allowed.has(id));
}

/** Splits the picked ids into the users and mailing groups the server expects. */
export function toSendBody(selected: string[], recipients: ReleaseNoteRecipient[], resend: boolean): ReleaseNoteSendBody {
  const groups = new Set(recipients.filter((r) => r.isGroup).map((r) => r.id));
  return {
    userIds: selected.filter((id) => !groups.has(id)),
    groupIds: selected.filter((id) => groups.has(id)),
    resend,
  };
}

export type SendBlocker = 'none-picked' | 'too-many' | null;

export function sendBlocker(count: number): SendBlocker {
  if (count === 0) return 'none-picked';
  if (count > MAX_RECIPIENTS) return 'too-many';
  return null;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function summarizeResult(result: ReleaseNoteSendResult): string {
  const parts = [`Sent to ${plural(result.sent.length, 'person', 'people')}`];
  if (result.skipped.length > 0) parts.push(`${result.skipped.length} already had it and were skipped`);
  if (result.failed.length > 0) parts.push(`${result.failed.length} failed`);
  return `${parts.join('. ')}.`;
}
